import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type { UploadProps } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  UploadOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons';
import { normalizeFileUrl, uploadFile } from '../../services/fileService';
import {
  createStaffTraining,
  deleteStaffTraining,
  listStaffTrainings,
  updateStaffTraining,
  type SaveStaffTrainingRequest,
  type StaffTrainingCourse,
  type StaffTrainingQuestion,
  type StaffTrainingQuestionType,
} from '../../services/staffTrainingService';

const { Text, Title } = Typography;

const questionTypeOptions: { label: string; value: StaffTrainingQuestionType }[] = [
  { label: '单选题', value: 'single' },
  { label: '多选题', value: 'multiple' },
  { label: '判断题', value: 'judge' },
  { label: '简答题', value: 'short_answer' },
];

const questionTypeText: Record<StaffTrainingQuestionType, string> = {
  single: '单选题',
  multiple: '多选题',
  judge: '判断题',
  short_answer: '简答题',
};

const defaultQuestion = (): any => ({
  id: `question_${Date.now()}_${Math.random().toString(16).slice(2, 8)}`,
  type: 'single',
  title: '',
  sort: 10,
  options: [
    { label: '选项A', value: 'A' },
    { label: '选项B', value: 'B' },
  ],
  answer: 'A',
});

const normalizeQuestionsForForm = (questions: StaffTrainingQuestion[]) => {
  return (questions || []).map(question => ({
    ...question,
    answer: question.type === 'multiple' ? question.answer || [] : question.answer?.[0] || '',
  }));
};

const normalizeQuestionsForSubmit = (questions: any[] = []): StaffTrainingQuestion[] => {
  return questions.map((question, index) => {
    const type = question.type as StaffTrainingQuestionType;
    const answer = Array.isArray(question.answer)
      ? question.answer
      : [String(question.answer || '').trim()].filter(Boolean);

    return {
      id: question.id || `question_${Date.now()}_${index}`,
      type,
      title: String(question.title || '').trim(),
      options:
        type === 'short_answer'
          ? []
          : type === 'judge'
            ? [
                { label: '正确', value: 'true' },
                { label: '错误', value: 'false' },
              ]
            : (question.options || []).map((option: any) => ({
                label: String(option?.label || '').trim(),
                value: String(option?.value || '').trim(),
              })),
      answer,
      matchRate: type === 'short_answer' ? Number(question.matchRate || 60) : 100,
      sort: Number(question.sort || (index + 1) * 10),
    };
  });
};

const buildAnswerOptions = (type: StaffTrainingQuestionType, options: any[] = []) => {
  if (type === 'judge') {
    return [
      { label: '正确', value: 'true' },
      { label: '错误', value: 'false' },
    ];
  }
  return (options || [])
    .filter(option => String(option?.label || '').trim() && String(option?.value || '').trim())
    .map(option => ({
      label: `${option.label}（${option.value}）`,
      value: option.value,
    }));
};

const StaffTraining: React.FC = () => {
  const [searchForm] = Form.useForm();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [records, setRecords] = useState<StaffTrainingCourse[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState<StaffTrainingCourse | null>(null);

  const loadData = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const data = await listStaffTrainings({
        page: nextPage,
        pageSize: nextPageSize,
        keyword: values.keyword,
        status: values.status ?? 'all',
      });
      setRecords(data.list || []);
      setTotal(Number(data.total || 0));
      setPage(nextPage);
      setPageSize(nextPageSize);
    } catch {
      message.error('加载服务培训失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData(1, pageSize);
  }, []);

  const openCreateModal = () => {
    setEditingRecord(null);
    form.setFieldsValue({
      title: '',
      description: '',
      videoUrl: '',
      videoName: '',
      requireWatchComplete: true,
      requirePassBeforeAccept: false,
      sort: 10,
      status: 1,
      questions: [defaultQuestion()],
    });
    setModalOpen(true);
  };

  const openEditModal = (record: StaffTrainingCourse) => {
    setEditingRecord(record);
    form.setFieldsValue({
      ...record,
      questions: normalizeQuestionsForForm(record.questions || []),
    });
    setModalOpen(true);
  };

  const handleVideoUpload: UploadProps['customRequest'] = async (options) => {
    const { file, onSuccess, onError } = options;
    setUploading(true);
    try {
      const response: any = await uploadFile(file as File, 'staff_training_video');
      const fileData = response.data || {};
      form.setFieldsValue({
        videoUrl: fileData.url || '',
        videoName: fileData.name || (file as File).name || '',
      });
      message.success('视频上传成功');
      onSuccess?.(fileData);
    } catch (error) {
      message.error('视频上传失败');
      onError?.(error as Error);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    const payload: SaveStaffTrainingRequest = {
      title: String(values.title || '').trim(),
      description: String(values.description || '').trim(),
      videoUrl: String(values.videoUrl || '').trim(),
      videoName: String(values.videoName || '').trim(),
      requireWatchComplete: values.requireWatchComplete !== false,
      requirePassBeforeAccept: values.requirePassBeforeAccept === true,
      questions: normalizeQuestionsForSubmit(values.questions || []),
      sort: Number(values.sort || 0),
      status: Number(values.status ?? 1),
    };

    setSaving(true);
    try {
      if (editingRecord) {
        await updateStaffTraining(editingRecord.id, payload);
        message.success('服务培训已保存');
      } else {
        await createStaffTraining(payload);
        message.success('服务培训已新增');
      }
      setModalOpen(false);
      void loadData(page, pageSize);
    } catch (error: any) {
      message.error(error?.response?.data?.message || '保存服务培训失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (record: StaffTrainingCourse) => {
    try {
      await deleteStaffTraining(record.id);
      message.success('服务培训已删除');
      void loadData(1, pageSize);
    } catch {
      message.error('删除服务培训失败');
    }
  };

  const columns: ColumnsType<StaffTrainingCourse> = [
    {
      title: '培训标题',
      dataIndex: 'title',
      width: 220,
      render: (value: string, record) => (
        <Space orientation="vertical" size={2}>
          <Text strong>{value}</Text>
          <Text type="secondary">{record.videoName || record.videoUrl || '-'}</Text>
        </Space>
      ),
    },
    {
      title: '题目数',
      dataIndex: 'questions',
      width: 100,
      render: (value: StaffTrainingQuestion[]) => `${value?.length || 0} 题`,
    },
    {
      title: '看完视频后答题',
      dataIndex: 'requireWatchComplete',
      width: 150,
      render: (value: boolean) => <Tag color={value ? 'blue' : 'default'}>{value ? '是' : '否'}</Tag>,
    },
    {
      title: '接单必须通过',
      dataIndex: 'requirePassBeforeAccept',
      width: 140,
      render: (value: boolean) => <Tag color={value ? 'red' : 'default'}>{value ? '开启' : '关闭'}</Tag>,
    },
    {
      title: '排序',
      dataIndex: 'sort',
      width: 90,
    },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: number) => <Tag color={value === 1 ? 'green' : 'default'}>{value === 1 ? '启用' : '禁用'}</Tag>,
    },
    {
      title: '更新时间',
      dataIndex: 'updatedAt',
      width: 180,
      render: (value: string) => value || '-',
    },
    {
      title: '操作',
      width: 160,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button type="link" icon={<EditOutlined />} onClick={() => openEditModal(record)}>
            编辑
          </Button>
          <Popconfirm title="确认删除该培训？" onConfirm={() => handleDelete(record)}>
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
          <Space>
            <VideoCameraOutlined />
            <Title level={2} style={{ margin: 0 }}>
              服务培训
            </Title>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => loadData(1, pageSize)}>
              刷新
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
              新增培训
            </Button>
          </Space>
        </Space>

        <Alert
          type="info"
          showIcon
          style={{ marginTop: 16 }}
          message="服务人员需要先看完培训视频后才可进入答题，题目答案将在服务小程序培训模块用于判题。"
        />

        <Form form={searchForm} layout="inline" style={{ marginTop: 20 }} initialValues={{ status: 'all' }}>
          <Form.Item name="keyword" label="关键词">
            <Input allowClear placeholder="培训标题/说明" style={{ width: 260 }} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select
              style={{ width: 140 }}
              options={[
                { label: '全部', value: 'all' },
                { label: '启用', value: 1 },
                { label: '禁用', value: 0 },
              ]}
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" onClick={() => loadData(1, pageSize)}>
              查询
            </Button>
          </Form.Item>
        </Form>

        <Table
          style={{ marginTop: 20 }}
          rowKey="id"
          loading={loading}
          columns={columns}
          dataSource={records}
          scroll={{ x: 980 }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            showTotal: count => `共 ${count} 条`,
            onChange: (nextPage, nextPageSize) => loadData(nextPage, nextPageSize),
          }}
        />
      </Card>

      <Modal
        title={editingRecord ? '编辑服务培训' : '新增服务培训'}
        open={modalOpen}
        width={980}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        confirmLoading={saving}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item name="title" label="培训标题" rules={[{ required: true, message: '请输入培训标题' }]}>
            <Input maxLength={100} placeholder="请输入培训标题" />
          </Form.Item>
          <Form.Item name="description" label="培训说明">
            <Input.TextArea rows={3} maxLength={500} showCount placeholder="请输入培训说明" />
          </Form.Item>
          <Space align="start" style={{ width: '100%' }}>
            <Form.Item name="videoUrl" label="培训视频" rules={[{ required: true, message: '请上传培训视频' }]}>
              <Input style={{ width: 520 }} placeholder="上传后自动填入视频地址" />
            </Form.Item>
            <Form.Item label="上传">
              <Upload accept="video/*" showUploadList={false} customRequest={handleVideoUpload}>
                <Button loading={uploading} icon={<UploadOutlined />}>
                  上传视频
                </Button>
              </Upload>
            </Form.Item>
          </Space>
          <Form.Item name="videoName" hidden>
            <Input />
          </Form.Item>
          <Form.Item shouldUpdate noStyle>
            {() => {
              const url = form.getFieldValue('videoUrl');
              return url ? (
                <video
                  src={normalizeFileUrl(url)}
                  controls
                  style={{ display: 'block', width: 320, maxWidth: '100%', marginBottom: 12, borderRadius: 8 }}
                />
              ) : null;
            }}
          </Form.Item>
          <Space size="large" align="start" style={{ display: 'flex', justifyContent: 'flex-start' }}>
            <Form.Item name="requireWatchComplete" label="看完视频后才可答题" valuePropName="checked">
              <Switch checkedChildren="是" unCheckedChildren="否" />
            </Form.Item>
            <Form.Item name="requirePassBeforeAccept" label="接单必须通过" valuePropName="checked">
              <Switch checkedChildren="开启" unCheckedChildren="关闭" />
            </Form.Item>
          </Space>
          <Space size="large">
            <Form.Item name="sort" label="排序">
              <InputNumber min={0} precision={0} />
            </Form.Item>
            <Form.Item name="status" label="状态">
              <Select
                style={{ width: 120 }}
                options={[
                  { label: '启用', value: 1 },
                  { label: '禁用', value: 0 },
                ]}
              />
            </Form.Item>
          </Space>

          <Divider>培训题目</Divider>
          <Form.List name="questions">
            {(fields, { add, remove }) => (
              <Space orientation="vertical" style={{ width: '100%' }} size={16}>
                {fields.map((field, index) => (
                  <Card
                    key={field.key}
                    size="small"
                    title={`第 ${index + 1} 题`}
                    extra={
                      fields.length > 1 ? (
                        <Button type="link" danger onClick={() => remove(field.name)}>
                          删除题目
                        </Button>
                      ) : null
                    }
                  >
                    <Form.Item name={[field.name, 'id']} hidden>
                      <Input />
                    </Form.Item>
                    <Space align="start" style={{ width: '100%' }}>
                      <Form.Item name={[field.name, 'type']} label="题型" rules={[{ required: true }]}>
                        <Select style={{ width: 140 }} options={questionTypeOptions} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'sort']} label="排序">
                        <InputNumber min={0} precision={0} />
                      </Form.Item>
                    </Space>
                    <Form.Item name={[field.name, 'title']} label="题目" rules={[{ required: true, message: '请输入题目' }]}>
                      <Input.TextArea rows={2} maxLength={500} showCount placeholder="请输入题目" />
                    </Form.Item>

                    <Form.Item shouldUpdate noStyle>
                      {() => {
                        const type = form.getFieldValue(['questions', field.name, 'type']) as StaffTrainingQuestionType;
                        if (type === 'short_answer') {
                          return (
                            <>
                              <Form.Item name={[field.name, 'answer']} label="参考答案" rules={[{ required: true, message: '请输入参考答案' }]}>
                                <Input.TextArea rows={2} placeholder="请输入关键词，支持逗号、顿号、分号或换行分隔" />
                              </Form.Item>
                              <Form.Item
                                name={[field.name, 'matchRate']}
                                label="通过匹配率（%）"
                                initialValue={60}
                                rules={[{ required: true, message: '请输入通过匹配率' }]}
                              >
                                <InputNumber min={1} max={100} precision={0} style={{ width: 180 }} />
                              </Form.Item>
                            </>
                          );
                        }
                        if (type === 'judge') {
                          return (
                            <Form.Item name={[field.name, 'answer']} label="正确答案" rules={[{ required: true, message: '请选择答案' }]}>
                              <Select
                                style={{ width: 180 }}
                                options={[
                                  { label: '正确', value: 'true' },
                                  { label: '错误', value: 'false' },
                                ]}
                              />
                            </Form.Item>
                          );
                        }

                        const options = form.getFieldValue(['questions', field.name, 'options']) || [];
                        return (
                          <>
                            <Form.List name={[field.name, 'options']}>
                              {(optionFields, optionActions) => (
                                <Space orientation="vertical" style={{ width: '100%' }}>
                                  {optionFields.map(optionField => (
                                    <Space key={optionField.key} align="start">
                                      <Form.Item
                                        name={[optionField.name, 'label']}
                                        rules={[{ required: true, message: '请输入选项内容' }]}
                                      >
                                        <Input style={{ width: 320 }} placeholder="选项内容" />
                                      </Form.Item>
                                      <Form.Item
                                        name={[optionField.name, 'value']}
                                        rules={[{ required: true, message: '请输入选项值' }]}
                                      >
                                        <Input style={{ width: 120 }} placeholder="选项值" />
                                      </Form.Item>
                                      {optionFields.length > 2 && (
                                        <Button danger onClick={() => optionActions.remove(optionField.name)}>
                                          删除
                                        </Button>
                                      )}
                                    </Space>
                                  ))}
                                  <Button
                                    type="dashed"
                                    onClick={() => optionActions.add({ label: '', value: '' })}
                                    icon={<PlusOutlined />}
                                  >
                                    添加选项
                                  </Button>
                                </Space>
                              )}
                            </Form.List>
                            <Form.Item name={[field.name, 'answer']} label="正确答案" rules={[{ required: true, message: '请选择答案' }]}>
                              <Select
                                mode={type === 'multiple' ? 'multiple' : undefined}
                                style={{ width: 360 }}
                                options={buildAnswerOptions(type, options)}
                                placeholder={`请选择${questionTypeText[type]}答案`}
                              />
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.Item>
                  </Card>
                ))}
                <Button type="dashed" block icon={<PlusOutlined />} onClick={() => add(defaultQuestion())}>
                  添加题目
                </Button>
              </Space>
            )}
          </Form.List>
        </Form>
      </Modal>
    </div>
  );
};

export default StaffTraining;
